import { Dayjs } from "dayjs";
import { Options, Weekday } from "rrule";

import { USER_STATUS } from "@/utils/constants";
import { TableFilters } from "./common";
import { Staff } from "./IAuth";
import { OriginalNote } from "./IDailyNotes";
import { CheckInCheckOutByID } from "./IRoster";

export interface Address {
  buildingName: string;
  addressLine1: string;
  addressLine2: string;
  town: string;
  country: string;
  postCode: string;
  shortAddress?: string;
}
export interface ServiceUser {
  _id: string;
  preferredName: string;
  title: string;
  avatar: string;
  firstName: string;
  middleName: string;
  lastName: string;
  name: string;
  gender: string;
  dateOfBirth: string;
  age: string;
  status: string;
  zone: string;
  address: Address;
  postCode: string;
  landlineNumber: string;
  telephoneNumber: string;
  keySafeNumber: string;
  assignedSite: string;
  assignedFloor: string;
  assignedRoom: string;
  assignedBed: string;
  niNumber: string;
  nhsNumber: string;
  socialServiceRef: string;
  assignedCarers: any[];
  weight: string;
  height: string;
  eyeColor: string;
  bodyType: string;
  hairColor: string;
  glassWare: string;
  hearingAid: string;
  contacts: any[];
  homeOwner: string;
  ethnicity: string;
  religion: string;
  sexualOrientation: string;
  maritalStatus: string;
  joiningDate: string;
  fundingSource: string;
  carerAlerts: { [key: string]: any };
  medicalSupport?: boolean;
  pharmacyName?: string;
  pharmacyAddress?: Omit<Address, "postCode">;
  pharmacyPostcode?: string;

  isGPSCheckInActive?: boolean;
  addressCoordinates?: { lat: number; lng: number };
  checkInRadius?: number;
}

export type CompactServiceUser = Pick<
  ServiceUser,
  | "_id"
  | "firstName"
  | "middleName"
  | "lastName"
  | "name"
  | "avatar"
  | "address"
  | "postCode"
  | "status"
  | "zone"
  | "isGPSCheckInActive"
  | "addressCoordinates"
  | "checkInRadius"
>;

export type ScheduleFrequency = "" | "daily" | "weekly" | "custom";

export enum ScheduleSessionEnum {
  MORNING = "morning",
  LUNCH = "lunch",
  AFTERNOON = "afternoon",
  EVENING = "evening",
  NIGHT = "night",
}

export enum VisitStatus {
  SCHEDULED = "scheduled",
  IN_PROGRESS = "in-progress",
  OVERDUE = "overdue",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
  UN_ALLOCATED = "",
}

export type ScheduleSession = "" | "morning" | "lunch" | "evening" | "night";

export interface Rrule {
  endDate: "";
  frequency?: ScheduleFrequency;
  byweekday?: Weekday["weekday"][];
  interval?: "day" | "week" | "in-week";
  intervalCount?: number;
  dtstart?: Date;
}

export interface AssignUsers {
  _id: string;
  selectedDays: Weekday["weekday"][];
}
export type ScheduleVisit = {
  _id?: string;
  rrule: Rrule;
  session: ScheduleSession;
  startTime: string | Dayjs | Date;
  endTime: string | Dayjs | null | Date;
  startDate: Date | null;
  endDate: Date | "never" | null;
  carerReq: number;
  exdate?: (string | Date)[];
  carerAssigned?: string[];
  assignUsers: AssignUsers[];
  anotherCarers?: { carer: string; type: string }[];
  notes?: string[];
};

export interface Visit {
  _id: string;
  origin?: ScheduleVisit;
  carerReq: number;
  carerAssigned: Staff[];
  anotherCarers?: {
    carer: Staff;
    type: string;
  }[];
  overrideReason?: string;
  serviceUser: CompactServiceUser | null;
  session: string;
  startDate: Date;
  endDate: Date | "never";
  startTime: Date;
  endTime: Date;
  rrule: Partial<Options>;
  originalRRule?: Rrule;
  exdate?: (string | Date)[];
  recurring?: boolean;
  duration: {
    text: string;
    raw: number;
  };
  notes?: OriginalNote[];
  createdAt: Date;
  updatedAt: Date;
  __v: number;
  date: Date;
  status?: VisitStatus;
  isCancelled?: boolean;
  assignUsers?: AssignUsers[];
  cancelOptions?: {
    chargeServiceUser: boolean;
    payStaff: string[];
    cancellationNote: string;
    cancelledBy?: Staff;
    cancelledAt?: Date;
  };
  logs?: CheckInCheckOutByID[];
  createdBySummary?: string | null;
  createdVia?: string | null;
  lastUpdatedBySummary?: string | null;
  lastUpdatedVia?: string | null;
  auditSummary?: {
    count: number;
    createdBy: string | null;
    createdVia: string | null;
    lastUpdatedBy: string | null;
    lastUpdatedVia: string | null;
  };
}

export interface VisitAuditEntry {
  _id: string;
  schemaVersion?: number;
  visitId?: string | null;
  rootVisitId?: string | null;
  originVisitId?: string | null;
  actionType:
    | "create"
    | "create_batch"
    | "upsert"
    | "update"
    | "update_batch"
    | "duplicate"
    | "cancel"
    | "delete"
    | "materialize"
    | "baseline";
  actionSource:
    | "user_ui"
    | "user_api"
    | "system_propagation"
    | "system_cron"
    | "system_migration"
    | "system_automatic"
    | "roster"
    | "unknown";
  outcome?:
    | "succeeded"
    | "failed"
    | "no_change"
    | "already_exists"
    | "partial"
    | "observed";
  requestId?: string | null;
  correlationId?: string | null;
  operationId?: string | null;
  tenantId?: string | null;
  application?: string | null;
  applicationVersion?: string | null;
  durationMs?: number | null;
  failure?: {
    name?: string | null;
    code?: string | null;
    message?: string | null;
    statusCode?: number | null;
  } | null;
  userId?: string | null;
  userName?: string | null;
  userType?: string | null;
  occurredAt: string;
  payloadSnapshot?: Record<string, unknown> | null;
  changeSummary?: Array<{
    field: string;
    oldValue: unknown;
    newValue: unknown;
    displayOldValue?: unknown;
    displayNewValue?: unknown;
    message?: string;
  }>;
  duplicationContext?: Record<string, unknown> | null;
}

export type LinkedVisitAuditTimelineEntry = Omit<
  Partial<VisitAuditEntry>,
  "actionType"
> & {
  recordType?: string;
  actionType?: VisitAuditEntry["actionType"] | "check_in" | "check_out";
  visitId?: string | null;
  rootVisitId?: string | null;
  sourceCollection?: string;
  recordId?: string | null;
  relatedRecordIds?: string[];
  occurredAt?: string | Date | null;
  recordedAt?: string | Date | null;
  actor?: {
    id?: string | null;
    name?: string | null;
    type?: string | null;
  };
  subject?: {
    id?: string | null;
    name?: string | null;
  } | null;
  ipAddress?: string | null;
  ipFamily?: string | null;
  proxyChain?: string[];
  ipResolution?: string | null;
  triggerContext?: Record<string, unknown> | null;
  details?: Record<string, unknown> | null;
  dataQuality?: string[];
};

export interface LinkedVisitAuditHistory {
  summary: {
    requestedVisitId: string;
    selectedVisitId?: string;
    liveRecordStatus: "present" | "deleted_or_unavailable";
    rootVisitId: string | null;
    relatedVisitIds: string[];
    selectedScope?: "root" | "occurrence" | "virtual_occurrence";
    selectedAuditCount?: number;
    selectedRosterEventCount?: number;
    seriesAuditCount?: number;
    auditCount: number;
    rosterEventCount: number;
  };
  selectedVisit?: {
    visitId: string;
    rootVisitId: string;
    originVisitId?: string | null;
    status: "live" | "deleted" | "virtual";
    occurrenceStart?: string | null;
    snapshot?: Record<string, unknown> | null;
  } | null;
  selectedTimeline?: LinkedVisitAuditTimelineEntry[];
  seriesVisit?: {
    visitId: string;
    rootVisitId: string;
    originVisitId?: string | null;
    status: "live" | "deleted" | "virtual";
    occurrenceStart?: string | null;
    snapshot?: Record<string, unknown> | null;
  } | null;
  seriesTimeline?: LinkedVisitAuditTimelineEntry[];
  relatedOccurrences?: Array<{ visitId: string; rootVisitId: string }>;
  timeline: LinkedVisitAuditTimelineEntry[];
  dataQuality: string[];
  pagination?: {
    page: number;
    limit: number;
    returned: number;
    hasMore: boolean;
    selected?: {
      page: number;
      limit: number;
      total: number;
      returned: number;
      hasMore: boolean;
    };
    series?: {
      page: number;
      limit: number;
      total: number;
      returned: number;
      hasMore: boolean;
    };
  };
}

export type VisitAuditSearchResultScope =
  "root" | "occurrence" | "standalone" | "deleted";

export interface VisitAuditSearchResult {
  visitId: string;
  resultScope?: VisitAuditSearchResultScope;
  rootVisitId?: string | null;
  originVisitId?: string | null;
  serviceUserId?: string | null;
  serviceUserName?: string | null;
  startDate?: string | Date | null;
  startTime?: string | Date | null;
  endTime?: string | Date | null;
  recurring?: boolean;
  assignedCarerIds?: string[];
  liveRecordStatus: "present" | "deleted_or_unavailable";
  auditRecordId?: string | null;
}

export type DayWiseVisits = {
  [ScheduleSessionEnum.MORNING]?: { [key: string]: Visit[] };
  [ScheduleSessionEnum.LUNCH]?: { [key: string]: Visit[] };
  [ScheduleSessionEnum.AFTERNOON]?: { [key: string]: Visit[] };
  [ScheduleSessionEnum.EVENING]?: { [key: string]: Visit[] };
  [ScheduleSessionEnum.NIGHT]?: { [key: string]: Visit[] };
};

export enum InActivityType {
  TEMPORARY = "temporary",
  PERMANENT = "permanent",
}

export interface InActivity {
  _id: string;
  user: string;
  type: InActivityType;
  reason: (typeof USER_STATUS)[number]["value"];
  start: Date;
  end: Date;
  createdAt: Date;
  updatedAt: Date;
  __v: number;
}

export enum TypeMedication {
  SCHEDULED = "scheduled",
  PRN = "prn",
  BLISTER_PACK = "blister-pack",
}

export enum PREFERRED_TIME {
  TIME_PERIOD = "time-period",
  SPECIFIC_TIME = "specific-time",
}

export type MedicationForm = {
  medicine: string;
  medicineCode: string;
  type: "" | TypeMedication;
  dose: string;
  route: string;
  startDate: Dayjs;
  endDate: Dayjs | null;
  howManyTimesADay: number;
  instructions: string;
  preferredTime: "" | PREFERRED_TIME;
  timePeriod: Array<string>;
  specificTime: Array<Dayjs>;
  showOnRoster?: boolean;
  rrule: {
    frequency?: ScheduleFrequency;
    byweekday?: Weekday["weekday"][];
    byhour?: number[];
    byminute?: number[];
    interval?: "day" | "week" | "in-week";
    intervalCount?: number;
  };
  supportOption: "" | "administer" | "assist" | "prompt";
  cqcGuidelineLink?: string;
};

export interface Medication {
  _id: string;
  rrule?: {
    frequency?: ScheduleFrequency;
    byweekday?: Weekday["weekday"][];
    byhour?: number[];
    byminute?: number[];
    interval?: "day" | "week" | "in-week";
    intervalCount?: number;
  };
  preferredTime?: PREFERRED_TIME;
  timePeriod: string[];
  specificTime: Date[];
  discontinued: boolean;
  discontinuedInfo?: {
    by: Staff;
    at: Date;
  };
  howManyTimesADay?: number;
  user: CompactServiceUser;
  medicine: string;
  medicineCode?: string;
  type: TypeMedication;
  dose: string;
  route: string;
  startDate: Date;
  endDate?: Date;
  createdBy: CompactServiceUser;
  instructions?: string;
  showOnRoster?: boolean;
  bodyParts?: string[];
  supportOption?: "administer" | "assist" | "prompt";
  cqcGuidelineLink?: string;
  createdAt: Date;
  updatedAt: Date;
  __v: number;
}

export interface MedicationFilters extends TableFilters {
  type?: TypeMedication | any;
  frequency?: ScheduleFrequency;
  route?: string;
  createdBy?: string;
  startDate?: string;
  endDate?: string;
  discontinued?: boolean;
}

export interface MedicationData {
  _id: string;
  timePeriod: string[];
  specificTime: Date[];
  discontinued: boolean;
  discontinuedInfo?: {
    by: Staff;
    at: Date;
  };
  medicine: string;
  medicineCode?: string;
  type: TypeMedication;
  dose: string;
  route: string;
  startDate: Date;
  endDate: Date | null;
  howManyTimesADay: number;
  instructions: string;
  preferredTime: string;
  rrule: Rrule;
  originalRRule?: Options;
  user: CompactServiceUser;
  showOnRoster?: boolean;
  createdBy: CompactServiceUser;
  supportOption?: "administer" | "assist" | "prompt";
  cqcGuidelineLink?: string;
  createdAt: Date;
  updatedAt: Date;
  __v: number;
}

export interface MedicationLog {
  _id: string;
  outcome: string;
  reason?: string;
  note?: string;
  user: CompactServiceUser;
  medication: Medication;
  resourceId: string;
  date: Date;
  createdBy: CompactServiceUser;
  updatedBy?: CompactServiceUser;
  createdAt: Date;
  updatedAt: Date;
  __v: number;
}

export interface TodayMedication {
  user: CompactServiceUser;
  medications: number;
  logs: number;
}
export interface Incompatibility {
  _id: string;
  serviceUser: string | CompactServiceUser;
  staff: Staff;
  reason: string;
  status: "active" | "inactive";
  history: Array<{
    reason: string;
    updatedBy: Staff;
    updatedAt: Date;
  }>;
  createdBy: Staff;
  updatedBy: Staff;
  createdAt: Date;
  updatedAt: Date;
}
